import React, { useState } from 'react';
import {
  Box, Typography, Button, CircularProgress, Collapse, Chip, Tooltip,
} from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import RefreshIcon from '@mui/icons-material/Refresh';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { bloomberg } from '../theme/bloombergTheme';
import { getStockPulse, StockPulse as Pulse } from '../services/api';

const CARD = {
  bgcolor: bloomberg.bg.secondary,
  border: `1px solid ${bloomberg.border.primary}`,
  borderRadius: 1,
  p: 2,
};

/**
 * "What's going on with this?" — a narrative assembled from the data the app
 * already holds (price/trend, scanner signals, chart patterns, linked news)
 * and written up by the locally-hosted model.
 *
 * Generation runs on CPU and takes 30-90s cold, so the wait is stated plainly
 * rather than hidden behind an indeterminate spinner, and the facts the model
 * was given are always inspectable underneath.
 */
export default function StockPulse({ symbol }: { symbol: string }) {
  const [pulse, setPulse] = useState<Pulse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showFacts, setShowFacts] = useState(false);
  const [elapsed, setElapsed] = useState(0);

  const run = async (refresh: boolean) => {
    setLoading(true);
    setError(null);
    setElapsed(0);
    const t = setInterval(() => setElapsed(e => e + 1), 1000);
    try {
      const p = await getStockPulse(symbol, refresh);
      setPulse(p);
      // Generation can time out on a busy box. When it does the facts are all
      // we have, so open them rather than leaving the user with an error and a
      // collapsed panel they have to discover.
      if (!p.narrative) setShowFacts(true);
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Could not generate an update for this stock.');
    } finally {
      clearInterval(t);
      setLoading(false);
    }
  };

  const ctx = pulse?.context;
  const factGroups: Array<[string, string[]]> = ctx
    ? [
        ['Price & trend', ctx.price_lines],
        ['Signals', ctx.signals],
        ['Patterns', ctx.patterns],
        ['Headlines', ctx.news],
      ].filter(([, v]) => (v as string[])?.length > 0) as Array<[string, string[]]>
    : [];

  return (
    <Box sx={CARD}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: pulse || loading ? 1.5 : 0 }}>
        <AutoAwesomeIcon sx={{ fontSize: 18, color: bloomberg.accent.orange }} />
        <Typography variant="subtitle1" sx={{ flex: 1 }}>What's going on with {symbol}?</Typography>

        {pulse && !loading && (
          <>
            {pulse.cached && (
              <Tooltip title={`Generated ${pulse.generated_at ? new Date(pulse.generated_at).toLocaleString() : 'earlier'}. Cached for 4 hours.`}>
                <Chip label="cached" size="small" variant="outlined"
                  sx={{ height: 18, fontSize: '0.6rem', color: bloomberg.text.muted }} />
              </Tooltip>
            )}
            <Tooltip title="Regenerate — takes 30-90 seconds">
              <span>
                <Button size="small" startIcon={<RefreshIcon sx={{ fontSize: 14 }} />}
                  onClick={() => run(true)}
                  sx={{ fontSize: '0.7rem', color: bloomberg.text.secondary }}>
                  Refresh
                </Button>
              </span>
            </Tooltip>
          </>
        )}

        {!pulse && !loading && (
          <Button size="small" variant="outlined" onClick={() => run(false)}
            sx={{ fontSize: '0.72rem', borderColor: bloomberg.accent.orange, color: bloomberg.accent.orange }}>
            Generate
          </Button>
        )}
      </Box>

      {loading && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1.5 }}>
          <CircularProgress size={18} sx={{ color: bloomberg.accent.orange }} />
          <Box>
            <Typography sx={{ fontSize: '0.82rem' }}>
              Reading this stock's price action, signals and recent news…
            </Typography>
            <Typography variant="caption" sx={{ color: bloomberg.text.muted }}>
              Running on the local model — usually 30-90 seconds. {elapsed}s elapsed.
            </Typography>
          </Box>
        </Box>
      )}

      {error && !loading && (
        <Typography sx={{ fontSize: '0.82rem', color: bloomberg.status.negative, mt: 1 }}>{error}</Typography>
      )}

      {pulse && !loading && (
        <Box>
          {/* Computed by rules, shown exactly as computed. This is the part
              that carries a judgement, so it deliberately never passes through
              the model — it is not in the prompt at all. */}
          {(ctx?.tensions?.length ?? 0) > 0 && (
            <Box sx={{
              mb: 1.75, p: 1.25, borderRadius: 1,
              bgcolor: 'rgba(245,158,11,0.08)',
              borderLeft: `3px solid ${bloomberg.status.warning}`,
            }}>
              <Typography variant="caption" sx={{
                color: bloomberg.status.warning, fontWeight: 700,
                textTransform: 'uppercase', letterSpacing: 0.5,
                display: 'block', mb: 0.5,
              }}>
                What doesn't line up
              </Typography>
              {ctx!.tensions.map((t, i) => (
                <Typography key={i} sx={{
                  fontSize: '0.83rem', lineHeight: 1.6,
                  display: 'flex', gap: 0.75,
                  mb: i === ctx!.tensions.length - 1 ? 0 : 0.5,
                }}>
                  <Box component="span" sx={{ color: bloomberg.status.warning }}>·</Box>
                  <Box component="span">{t}</Box>
                </Typography>
              ))}
            </Box>
          )}

          {pulse.narrative ? (
            pulse.narrative.split(/\n\s*\n/).filter(Boolean).map((para, i) => (
              <Typography key={i} sx={{ fontSize: '0.86rem', lineHeight: 1.65, mb: 1.25 }}>
                {para.trim()}
              </Typography>
            ))
          ) : (
            <Typography sx={{ fontSize: '0.82rem', color: bloomberg.text.muted, mb: 1 }}>
              {pulse.error || 'No narrative available — the underlying data is below.'}
            </Typography>
          )}

          {factGroups.length > 0 && (
            <>
              <Button size="small" onClick={() => setShowFacts(v => !v)}
                endIcon={<ExpandMoreIcon sx={{
                  fontSize: 16,
                  transform: showFacts ? 'rotate(180deg)' : 'none',
                  transition: 'transform .2s',
                }} />}
                sx={{ fontSize: '0.7rem', color: bloomberg.text.muted, px: 0, mt: 0.5 }}>
                {showFacts ? 'Hide' : 'Show'} the {factGroups.reduce((n, [, v]) => n + v.length, 0)} facts this was written from
              </Button>

              <Collapse in={showFacts}>
                <Box sx={{
                  mt: 1, pt: 1.5, borderTop: `1px solid ${bloomberg.border.primary}`,
                  display: 'flex', flexDirection: 'column', gap: 1.5,
                }}>
                  {factGroups.map(([title, items]) => (
                    <Box key={title}>
                      <Typography variant="caption" sx={{
                        color: bloomberg.accent.blue, fontWeight: 700,
                        textTransform: 'uppercase', letterSpacing: 0.5,
                      }}>
                        {title}
                      </Typography>
                      {items.map((line, i) => (
                        <Typography key={i} sx={{
                          fontSize: '0.76rem', color: bloomberg.text.secondary,
                          lineHeight: 1.55, pl: 1,
                        }}>
                          · {line}
                        </Typography>
                      ))}
                    </Box>
                  ))}
                  <Typography variant="caption" sx={{ color: bloomberg.text.muted, fontStyle: 'italic' }}>
                    The summary above was written by the self-hosted model from these
                    facts only — it has no internet access and cannot add information
                    beyond what is listed here. Anything under "What doesn't line up" is
                    computed directly from the data and is not model-generated.
                    {pulse.elapsed_seconds ? ` Generated in ${pulse.elapsed_seconds}s.` : ''}
                  </Typography>
                </Box>
              </Collapse>
            </>
          )}
        </Box>
      )}

      {!pulse && !loading && !error && (
        <Typography sx={{ fontSize: '0.8rem', color: bloomberg.text.muted, mt: 1 }}>
          Pulls together this stock's price action, recent scanner signals, chart
          patterns and linked news into a short written update.
        </Typography>
      )}
    </Box>
  );
}
